import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  input,
  output,
  viewChild,
} from '@angular/core';
import { ButtonComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslatePipe } from '../../../../core/i18n';
import { AlertToast } from '../../../../shared/components/alert-toast/alert-toast';
import type { AddressSuggestionMode } from '../../utils/map-validate-results-to-suggestions';

export type AddressSuggestionRole = 'sender' | 'recipient' | 'returnTo';

/**
 * Displays AVS suggested-address actions for a create-shipment address section.
 * Invalid-without-suggestion (OM-513) uses a surcharge warning and Keep My Entry only.
 */
@Component({
  selector: 'app-address-suggested-address-panel',
  imports: [ButtonComponent, TypographyComponent, TranslatePipe, AlertToast],
  templateUrl: './address-suggested-address-panel.html',
  styleUrl: './address-suggested-address-panel.scss',
})
export class AddressSuggestedAddressPanel {
  private readonly panelRoot = viewChild.required<ElementRef<HTMLElement>>('panelRoot');

  readonly sectionRole = input.required<AddressSuggestionRole>();
  readonly panelId = input.required<string>();
  readonly displayLine = input.required<string>();
  readonly mode = input<AddressSuggestionMode>('suggestion');

  readonly useSuggested = output<void>();
  readonly keepEntry = output<void>();

  /** True when AVS returned invalid with no actionable suggestion. */
  protected readonly isUnverifiedWarning = computed(() => this.mode() === 'unverifiedWarning');

  /** DOM id for the panel title referenced by aria-labelledby. */
  protected readonly titleId = computed(() => `${this.panelId()}-title`);

  /** DOM id for the suggested-address copy referenced by the street field aria-describedby. */
  protected readonly descriptionId = computed(() => `${this.panelId()}-description`);

  /** Warning mode labels by the surcharge copy; suggestion mode labels by the section title. */
  protected readonly labelledById = computed(() =>
    this.isUnverifiedWarning() ? this.descriptionId() : this.titleId(),
  );

  constructor() {
    afterNextRender(() => {
      this.panelRoot().nativeElement.focus({ preventScroll: false });
    });
  }

  /**
   * Returns the i18n key for the section-specific verification title.
   */
  protected titleKey(): string {
    return `createShipment.suggestedAddress.title.${this.sectionRole()}`;
  }

  /**
   * Emits when the user accepts the AVS suggested address.
   */
  protected onUseSuggested(): void {
    this.useSuggested.emit();
  }

  /**
   * Emits when the user keeps their original address entry.
   */
  protected onKeepEntry(): void {
    this.keepEntry.emit();
  }
}
